import React from 'react';
import { FieldPath, UseFormReturn } from 'react-hook-form';
import { SeoLockedFaqsNotice, StyledSeoNote } from '../../../SeoLockedContent';
import { StyledSectionTitle } from '../../../../PlusWebsiteEditor/PlusWebsiteEditor.styles';
import { ColumnWithGap } from '../../../../PlusWebsiteEditor/sections/HeroBlock/HeroBlock.styled';
import { ClinicWithConditionsAndPromo } from '../../../types';
import TextEditor from '../../../../FormEditors/TextEditor';
import HtmlEditor from '../../../../FormEditors/HtmlEditor';
import ImageEditor from '../../../../FormEditors/ImageEditor';
import ColorEditor from '../../../../FormEditors/ColorEditor';
import RepeatableRowsEditor from '../../../../FormEditors/RepeatableRowsEditor';
import ClinicalReviewEditor from '../../../ClinicalReviewEditor';
import styled from 'styled-components';

type ClinicConditionPromoEditorProps = Readonly<{
  form: UseFormReturn<ClinicWithConditionsAndPromo>;
  index: number;
  isDisabled?: boolean;
}>;

// Dynamic per-row field paths (from RepeatableRowsEditor) can't be statically inferred; cast them.
type ClinicFieldName = FieldPath<ClinicWithConditionsAndPromo>;

function ClinicConditionPromoEditor(props: ClinicConditionPromoEditorProps) {
  return (
    <>
      <StyledSectionTitle>Promotional Content</StyledSectionTitle>
      <PromoScreenEditor {...props} />
      <ShopCardEditor {...props} />
      <HeroFieldsEditor {...props} />
      <TreatmentRowEditor {...props} />
      <PageColoursEditor {...props} />
      <HeroBenefitsEditor {...props} />
      <AboutConditionEditor {...props} />
      <HowItWorksEditor {...props} />
      <FaqsEditor {...props} />
      <CtaBannerEditor {...props} />
      <ClinicalReviewEditor
        form={props.form}
        name={`clinic_conditions.${props.index}.promotional_content`}
        pageLabel="Clinically reviewed by"
        page="this condition page"
        isDisabled={props.isDisabled}
      />
    </>
  );
}

function HeroFieldsEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Hero</StyledSectionTitle>
      <TextEditor
        label="Hero title"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.hero_title`}
        disabled={isDisabled}
      />
      <StyledSeoNote>
        🔒 Partner pharmacy websites replace this title with the generated SEO H1 (&quot;[Condition] Treatment in
        [town]&quot;). This title is used on the Healthera site.
      </StyledSeoNote>
    </ColumnWithGap>
  );
}

// The For Her / For Him row on the clinic page. "Row text" is the field PAM used to label "Hero subtitle":
// the row is the only place it shows (the condition hero uses the promo description).
function TreatmentRowEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Clinic page treatment row</StyledSectionTitle>
      <StyledSeoNote>
        Shown in the For Her / For Him rows on the clinic page. The photos are also used by the condition page
        hero, following the tab the visitor picked.
      </StyledSeoNote>
      <StyledSpacedFields>
        <TextEditor
          label="Headline"
          control={form.control}
          name={`clinic_conditions.${index}.promotional_content.headline`}
          optional
          disabled={isDisabled}
        />
        <TextEditor
          label="Row text"
          control={form.control}
          name={`clinic_conditions.${index}.promotional_content.subtitle`}
          optional
          disabled={isDisabled}
        />
        <StyledImagesRow>
        <ImageEditor
          control={form.control}
          name={`clinic_conditions.${index}.promotional_content.gender_images.female`}
          label="Image for her"
          suggestedSize="900x900px"
          height="180px"
          width="180px"
        />
        <ImageEditor
          control={form.control}
          name={`clinic_conditions.${index}.promotional_content.gender_images.male`}
          label="Image for him"
          suggestedSize="900x900px"
          height="180px"
          width="180px"
        />
        </StyledImagesRow>
      </StyledSpacedFields>
    </ColumnWithGap>
  );
}

function PageColoursEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Page colours</StyledSectionTitle>
      <StyledSeoNote>
        The primary colour tints the treatment row and the condition tile; the secondary colour is the second colour
        behind the condition page hero. While no primary colour is set the pages use the background colour above.
      </StyledSeoNote>
      <StyledSpacedFields>
        <ColorEditor
          control={form.control}
          name={`clinic_conditions.${index}.promotional_content.primary_color`}
          label="Primary color (#hex)"
          disabled={isDisabled}
        />
        <ColorEditor
          control={form.control}
          name={`clinic_conditions.${index}.promotional_content.secondary_color`}
          label="Secondary color (#hex)"
          disabled={isDisabled}
        />
      </StyledSpacedFields>
    </ColumnWithGap>
  );
}

function AboutConditionEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>About condition</StyledSectionTitle>
      <StyledSeoNote>Accordion on the condition page; the first item is shown open.</StyledSeoNote>
      <RepeatableRowsEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.about_condition`}
        label="Items"
        addButtonLabel="Add item"
        emptyMessage="No items yet. The section is hidden until one is added."
        disabled={isDisabled}
        createEmptyRow={() => ({ question: '', answer: '' })}
        renderRow={(rowName) => (
          <StyledSpacedFields>
            <TextEditor
              label="Question"
              control={form.control}
              name={`${rowName}.question` as ClinicFieldName}
              disabled={isDisabled}
            />
            <HtmlEditor label="Answer" control={form.control} name={`${rowName}.answer` as ClinicFieldName} />
          </StyledSpacedFields>
        )}
      />
    </ColumnWithGap>
  );
}

// Each editor puts its label just above its input, so stacked fields need a wider gap than that or a label
// reads as belonging to the field above.
const StyledSpacedFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;
`;

const StyledImagesRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;

  & > * {
    flex: 0 0 220px;
  }
`;

function HeroBenefitsEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Hero benefits (override)</StyledSectionTitle>
      <StyledSeoNote>
        Overrides the condition hero only; the green benefit bar always shows the clinic-level benefits.
      </StyledSeoNote>
      <StyledSeoNote>
        🔒 On partner pharmacy websites the THIRD benefit is replaced by a generated collection/delivery
        benefit (appended when fewer than three are authored); other benefits render as authored.
      </StyledSeoNote>
      <RepeatableRowsEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.hero_benefits`}
        label="Benefits"
        addButtonLabel="Add benefit"
        emptyMessage="Leave empty to use the clinic-level benefits"
        disabled={isDisabled}
        createEmptyRow={() => ({ icon_url: '', text: '' })}
        renderRow={(rowName) => (
          <>
            <ImageEditor
              control={form.control}
              name={`${rowName}.icon_url` as ClinicFieldName}
              label="Icon"
              suggestedSize="60x60px"
              height="60px"
              width="60px"
            />
            <TextEditor
              label="Text"
              control={form.control}
              name={`${rowName}.text` as ClinicFieldName}
              disabled={isDisabled}
            />
          </>
        )}
      />
    </ColumnWithGap>
  );
}

function HowItWorksEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>How it works</StyledSectionTitle>
      <StyledSeoNote>
        🔒 On partner pharmacy websites the LAST step&apos;s description is replaced by a generated
        collection/delivery sentence; earlier steps render as authored.
      </StyledSeoNote>
      <TextEditor
        label="Section title"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.how_it_works.title`}
        optional
        disabled={isDisabled}
      />
      <TextEditor
        label="Section subtitle"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.how_it_works.subtitle`}
        optional
        disabled={isDisabled}
      />
      <RepeatableRowsEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.how_it_works.steps`}
        label="Steps"
        addButtonLabel="Add step"
        disabled={isDisabled}
        createEmptyRow={() => ({ title: '', description: '', time_label: '', icon_url: '' })}
        renderRow={(rowName) => (
          <>
            <TextEditor label="Step title" control={form.control} name={`${rowName}.title` as ClinicFieldName} disabled={isDisabled} />
            <TextEditor label="Step description" control={form.control} name={`${rowName}.description` as ClinicFieldName} disabled={isDisabled} />
            <TextEditor label="Time label" control={form.control} name={`${rowName}.time_label` as ClinicFieldName} optional disabled={isDisabled} />
            <ImageEditor
              control={form.control}
              name={`${rowName}.icon_url` as ClinicFieldName}
              label="Step icon"
              suggestedSize="60x60px"
              height="60px"
              width="60px"
              disabled={isDisabled}
            />
          </>
        )}
      />
    </ColumnWithGap>
  );
}

function FaqsEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>FAQs</StyledSectionTitle>
      <SeoLockedFaqsNotice page="condition" />
      <RepeatableRowsEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.faqs`}
        label="Questions"
        addButtonLabel="Add FAQ"
        disabled={isDisabled}
        createEmptyRow={() => ({ question: '', answer: '' })}
        renderRow={(rowName) => (
          <>
            <TextEditor
              label="Question"
              control={form.control}
              name={`${rowName}.question` as ClinicFieldName}
              disabled={isDisabled}
            />
            <HtmlEditor label="Answer" control={form.control} name={`${rowName}.answer` as ClinicFieldName} />
          </>
        )}
      />
    </ColumnWithGap>
  );
}

function CtaBannerEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>CTA banner</StyledSectionTitle>
      <ImageEditor
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.image_url`}
        label="Banner image"
        suggestedSize="1125x630px"
        height="210px"
        width="375px"
      />
      <TextEditor
        label="Heading"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.heading`}
        disabled={isDisabled}
      />
      <TextEditor
        label="Subtitle"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.subtitle`}
        disabled={isDisabled}
      />
      <TextEditor
        label="Button label"
        control={form.control}
        name={`clinic_conditions.${index}.promotional_content.cta_banner.button.label`}
        disabled={isDisabled}
      />
    </ColumnWithGap>
  );
}

function PromoScreenEditor(props: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Promo screen</StyledSectionTitle>
      <PromoTitleEditor {...props} />
      <PromoDescriptionEditor {...props} />
      <PromoDisclaimerEditor {...props} />
      <PromoImageEditor {...props} />
      <PromoColorEditor {...props} />
    </ColumnWithGap>
  );
}

function PromoTitleEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <TextEditor
      label="Promo title"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.title`}
      disabled={isDisabled}
    />
  );
}

function PromoDescriptionEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <HtmlEditor
      label="Description"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.description`}
      disabled={isDisabled}
    />
  );
}

function PromoDisclaimerEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <HtmlEditor
      label="Disclaimer"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.disclaimer`}
      disabled={isDisabled}
    />
  );
}

function PromoImageEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ImageEditor
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.image_url`}
      suggestedSize="1125x630"
      label="Promo screen image"
      height="210px"
      width="375px"
      disabled={isDisabled}
    />
  );
}

function PromoColorEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ColorEditor
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.background_color`}
      label="Background color (#hex)"
      disabled={isDisabled}
    />
  );
}

function ShopCardEditor(props: ClinicConditionPromoEditorProps) {
  return (
    <ColumnWithGap>
      <StyledSectionTitle>Shop card promotion</StyledSectionTitle>
      <ShopCardTitleEditor {...props} />
      <ShopCardTaglineEditor {...props} />
      <ShopCardDescription {...props} />
      <ShopCardImage {...props} />
    </ColumnWithGap>
  );
}

function ShopCardTitleEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <TextEditor
      label="Shop card title"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.shop_card.title`}
      disabled={isDisabled}
    />
  );
}

function ShopCardTaglineEditor({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <TextEditor
      label="Shop card tagline"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.shop_card.tagline`}
      optional
      disabled={isDisabled}
    />
  );
}

function ShopCardImage({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <ImageEditor
      control={form.control}
      label="Shop card image"
      name={`clinic_conditions.${index}.promotional_content.shop_card.image_url`}
      suggestedSize="444x249"
      height="125px"
      width="222px"
      disabled={isDisabled}
    />
  );
}

function ShopCardDescription({ form, index, isDisabled }: ClinicConditionPromoEditorProps) {
  return (
    <HtmlEditor
      label="Shop card description"
      control={form.control}
      name={`clinic_conditions.${index}.promotional_content.shop_card.description`}
      disabled={isDisabled}
    />
  );
}

export default ClinicConditionPromoEditor;
